import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { Download, Upload, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function ServerNameMappingsPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("partners", "write");
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ server_name: "", partner_name: "" });
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/server-name-mappings");
            setRows(data || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.server_name.trim() || !form.partner_name.trim()) {
            return toast.error("Both fields are required");
        }
        try {
            await api.post("/server-name-mappings", form);
            toast.success("Mapping saved");
            setForm({ server_name: "", partner_name: "" });
            setOpen(false);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this mapping?")) return;
        try {
            await api.delete(`/server-name-mappings/${id}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const downloadTemplate = async () => {
        const token = localStorage.getItem("erp_access_token");
        const res = await fetch(`${API}/server-name-mappings/template`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "server_name_mappings_template.csv";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const onUploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/server-name-mappings/upload", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            const skipped = (data.skipped || []).length;
            toast.success(`Imported — ${data.inserted} new, ${data.updated} updated${skipped ? `, ${skipped} skipped` : ""}`);
            if (skipped) toast.warning(`Skipped: ${data.skipped.slice(0, 3).join("; ")}${data.skipped.length > 3 ? "…" : ""}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally { e.target.value = ""; }
    };

    const applyToExisting = async () => {
        if (!window.confirm("Re-apply all mappings to every stored daily upload and rebuild non-posted cycles? This may take a moment.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/server-name-mappings/apply-to-existing");
            toast.success(`Remapped ${data.uploads_remapped} upload(s) and rebuilt cycles`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const filtered = rows.filter((r) =>
        r.server_name.toLowerCase().includes(search.toLowerCase()) ||
        r.partner_name.toLowerCase().includes(search.toLowerCase()),
    );

    return (
        <div data-testid="server-name-mappings-page">
            <PageHeader
                title="Server Name Mappings"
                eyebrow="Purchase Reconciliation"
                description="Map short server names to canonical Partner names so daily uploads aggregate under the right partner."
                action={canWrite ? (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={downloadTemplate} className="rounded-sm h-9" data-testid="snm-template-btn"><Download size={13} className="mr-1.5"/>Template</Button>
                        <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onUploadFile} hidden data-testid="snm-import-file"/>
                        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-sm h-9" data-testid="snm-import-btn"><Upload size={13} className="mr-1.5"/>Bulk import</Button>
                        <Button variant="outline" size="sm" onClick={applyToExisting} disabled={busy} className="rounded-sm h-9" data-testid="snm-apply-existing-btn"><RefreshCw size={13} className={`mr-1.5 ${busy ? "animate-spin" : ""}`}/>Apply to existing uploads</Button>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="snm-add-btn">+ New mapping</Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-lg" data-testid="snm-dialog">
                                <DialogHeader><DialogTitle>Add mapping</DialogTitle></DialogHeader>
                                <div className="grid gap-3">
                                    <Field label="Server Name (short)">
                                        <Input value={form.server_name} onChange={(e) => setForm({ ...form, server_name: e.target.value })} placeholder="e.g. 1Fone Global" className="rounded-sm h-9" data-testid="snm-server-input"/>
                                    </Field>
                                    <Field label="Canonical Partner Name">
                                        <Input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} placeholder="e.g. 1FONE GLOBAL PTE LTD" className="rounded-sm h-9" data-testid="snm-partner-input"/>
                                    </Field>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="snm-save-btn">Save</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                ) : null}
            />

            <div className="flex items-center gap-3 mb-3">
                <Input placeholder="Search server or partner name…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm h-9 w-72" data-testid="snm-search"/>
                <span className="text-[11px] text-slate-500">{filtered.length} of {rows.length} mapping(s)</span>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[13px] financial min-w-[700px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Server Name (in upload)</th>
                            <th className="px-3 py-2.5">→</th>
                            <th className="px-3 py-2.5">Canonical Partner Name</th>
                            <th className="px-3 py-2.5 w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={4} className="text-center text-slate-500 py-10">No mappings yet</td></tr>}
                        {filtered.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100" data-testid={`snm-row-${r.id}`}>
                                <td className="px-3 py-2 font-mono text-[12.5px]">{r.server_name}</td>
                                <td className="px-3 py-2 text-slate-400">→</td>
                                <td className="px-3 py-2 font-medium">{r.partner_name}</td>
                                <td className="px-3 py-2">
                                    {canWrite && <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-600" data-testid={`snm-delete-${r.id}`}><Trash2 size={13}/></Button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
